// P7.6: „Szybkie reguły" — czyste definicje presetów cenowych włączanych togglem
// w PriceRules oraz budowa payloadu POST/PUT /api/price-rules. Wiersz presetowy
// rozpoznajemy po rule.presetKey (kolumna price_rules.preset_key, migracja 029).
// Wyłączenie presetu = active=false, nigdy DELETE.

// Najniższa korekta procentowa reguły, lustro lib/pricing.js#MIN_PROCENT_REGULY
// (zgodność pilnuje Login/backend/tests/regulyNaPakietachDb.test.js).
export const MIN_PROCENT_REGULY = -100;

function pad2(n) {
  return String(n).padStart(2, '0');
}

function ymd(y, m, d) {
  return `${y}-${pad2(m)}-${pad2(d)}`;
}

// Sezon wysoki: 20.12–01.03 (przez granicę roku). Zwraca najbliższe okno licząc
// od `today`: w trakcie sezonu — okno bieżące, poza nim — najbliższe przyszłe.
export function seasonRange(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();
  if (m < 3 || (m === 3 && d === 1)) {
    return { date_from: ymd(y - 1, 12, 20), date_to: ymd(y, 3, 1) };
  }
  return { date_from: ymd(y, 12, 20), date_to: ymd(y + 1, 3, 1) };
}

// Poza sezonem = dopełnienie okna sezonowego: 02.03–19.12 jednego roku
// (jeden ciągły zakres — tyle umie pojedyncza reguła price_rules).
export function offseasonRange(today = new Date()) {
  const y = today.getFullYear();
  const m = today.getMonth() + 1;
  const d = today.getDate();
  if (m === 12 && d >= 20) {
    return { date_from: ymd(y + 1, 3, 2), date_to: ymd(y + 1, 12, 19) };
  }
  return { date_from: ymd(y, 3, 2), date_to: ymd(y, 12, 19) };
}

// `name` ląduje w price_rules.name (dane, spójnie z polskimi nazwami sugestii
// P7.3); etykiety UI idą przez t('priceRules.presets.*'). Chipy = wartości %
// (NIE suwak); klucze ≤30 znaków (limit kolumny preset_key).
//
// `dates` to tylko PODPOWIEDŹ startowa (kiedyś twardo zaszyta na sezon narciarski
// 20.12–01.03) — wypożyczalnia rowerów czy kajaków ma sezon wysoki latem, nie zimą.
// Faktyczny zakres dat dla `season_high`/`offseason_promo` admin ustawia sam w dwóch
// polach daty w PriceRules.js (`presetDates` state); ta funkcja daje tylko wartość
// początkową, żeby pola nie startowały puste. `hasEditableDates` steruje tym, czy
// UI w ogóle pokazuje te pola (weekend nie ma dat, tylko dni tygodnia).
export const PRICE_PRESETS = [
  {
    key: 'weekend',
    name: 'Dopłata weekendowa',
    weekdays: [6, 7],
    dates: null,
    hasEditableDates: false,
    chips: [10, 15, 20],
    defaultChip: 15,
  },
  {
    key: 'season_high',
    name: 'Sezon wysoki',
    weekdays: null,
    dates: seasonRange,
    hasEditableDates: true,
    chips: [10, 15, 20],
    defaultChip: 15,
  },
  {
    key: 'offseason_promo',
    name: 'Promocja poza sezonem',
    weekdays: null,
    dates: offseasonRange,
    hasEditableDates: true,
    chips: [-10, -15, -20],
    defaultChip: -10,
  },
];

// Pełny payload POST/PUT /api/price-rules dla presetu. Dla presetów z datami
// (`hasEditableDates`) WYMAGANE jest podanie `dateFrom`/`dateTo` — to admin
// decyduje, czym jest jego sezon, a nie kod. Bez podania zakresu (np. preset
// bez dat typu weekend) `preset.dates` służy tylko jako fallback dla wywołań
// spoza UI (np. testów) — normalny przepływ zawsze przekazuje realny zakres.
// `includesBundles` (N.2): tylko gdy użytkownik dotknął przełącznika. Brak klucza w ciele zostawia
// wartość serwera, więc szybka reguła nie zmienia flagi sama z siebie.
export function presetRulePayload(presetKey, adjustmentValue, { today = new Date(), active = true, dateFrom, dateTo, includesBundles } = {}) {
  const preset = PRICE_PRESETS.find((p) => p.key === presetKey);
  if (!preset) return null;
  let range = { date_from: null, date_to: null };
  if (dateFrom !== undefined || dateTo !== undefined) {
    range = { date_from: dateFrom || null, date_to: dateTo || null };
  } else if (preset.dates) {
    range = preset.dates(today);
  }
  return {
    name: preset.name,
    active,
    date_from: range.date_from,
    date_to: range.date_to,
    weekdays: preset.weekdays,
    class_id: null,
    type_id: null,
    adjustment_type: 'percent',
    adjustment_value: adjustmentValue,
    priority: 0,
    preset_key: preset.key,
    ...(includesBundles === undefined ? {} : { includes_bundles: includesBundles }),
  };
}

// Własny procent w „Szybkich regułach” (24.10.2026): obok chipów firma może wpisać dowolną
// wartość. Kierunek (dopłata albo rabat) wynika z presetu, nie ze znaku wpisanego przez
// użytkownika, więc w polu wpisuje się samą liczbę („25” w promocji to −25%). Rabat musi
// zostawić cenę dodatnią, a dopłata ma rozsądny sufit, bo literówka („150” zamiast „15”)
// od razu zmienia ceny w widgecie.
export const PRESET_CUSTOM_LIMITS = { surcharge: 300, discount: 95 };

export function presetDirection(preset) {
  const pierwszy = Array.isArray(preset?.chips) && preset.chips.length ? preset.chips[0] : 1;
  return pierwszy < 0 ? -1 : 1;
}

export function presetCustomMax(preset) {
  return presetDirection(preset) < 0 ? PRESET_CUSTOM_LIMITS.discount : PRESET_CUSTOM_LIMITS.surcharge;
}

/**
 * Tekst z pola „własny procent” → `{ value }` (ze znakiem presetu, do 2 miejsc po przecinku)
 * albo `{ error: 'invalid' | 'range', max }`. Przyjmuje przecinek, kropkę, spacje, znak %
 * oraz znak + lub − na początku (ignorowany: kierunek ustala preset).
 */
export function parseCustomPercent(text, preset) {
  const max = presetCustomMax(preset);
  const surowy = String(text ?? '').replace(/\s+/g, '').replace(/%$/, '').replace(/^[+\-−]/, '').replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(surowy)) return { error: 'invalid', max };
  const liczba = Number(surowy);
  if (!(liczba > 0) || liczba > max) return { error: 'range', max };
  return { value: presetDirection(preset) * Math.round(liczba * 100) / 100 };
}
